"use client";

import React from "react";
import { Mail, HeartHandshake, CircleCheck, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const referralInfo = [{
  title: "Clinical Record Review",
  description: "Clear, personalized explanations of PSA trends, biopsy results, and pathology findings.",
  icon: CircleCheck
}, {
  title: "Treatment Path Analysis",
  description: "Understand your options, including active surveillance, surgery, and radiation, and how each may impact your quality of life.",
  icon: HeartHandshake
}, {
  title: "Care Team Preparation",
  description: "Feel confident going into your appointments with the right questions and a clear understanding of your next steps.",
  icon: Mail
}];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="89971" className="flex flex-col items-center justify-center py-8 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-20 bg-indigo-500/10 rounded-full flex items-center justify-center mb-4">
          <CircleCheck className="size-10 text-indigo-500" />
        </div>
        <h3 className="text-2xl font-serif font-bold text-foreground">
          Consultation Requested
        </h3>
        <p className="text-muted-foreground text-lg max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-8 rounded-full px-8 h-12 border-indigo-500/20 hover:bg-indigo-500/5 text-indigo-500">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField control={control} name="name" rules={{
        required: "Please enter your name"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-base font-medium ml-1">
                Your Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Mark Smith" className="h-14 px-5 rounded-2xl bg-muted/40 border-transparent focus-visible:border-indigo-500 focus-visible:bg-background transition-colors text-base shadow-inner" {...field} />
              </FormControl>
              <FormMessage className="ml-1" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-base font-medium ml-1">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="Mark@example.com" className="h-14 px-5 rounded-2xl bg-muted/40 border-transparent focus-visible:border-indigo-500 focus-visible:bg-background transition-colors text-base shadow-inner" {...field} />
              </FormControl>
              <FormMessage className="ml-1" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[0-9\-\+\s\(\)]*$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-base font-medium ml-1">
                Phone Number{" "}
                <span className="text-muted-foreground font-normal text-sm">
                  (Optional)
                </span>
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" className="h-14 px-5 rounded-2xl bg-muted/40 border-transparent focus-visible:border-indigo-500 focus-visible:bg-background transition-colors text-base shadow-inner" {...field} />
              </FormControl>
              <FormMessage className="ml-1" />
            </FormItem>} />

        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-base font-medium ml-1">
                Stage of Journey{" "}
                <span className="text-muted-foreground font-normal text-sm">
                  (Optional)
                </span>
              </FormLabel>
              <FormControl>
                <Input placeholder="e.g., Newly Diagnosed" className="h-14 px-5 rounded-2xl bg-muted/40 border-transparent focus-visible:border-indigo-500 focus-visible:bg-background transition-colors text-base shadow-inner" {...field} />
              </FormControl>
              <FormMessage className="ml-1" />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Please provide a message so I can help you"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-base font-medium ml-1">
              Message
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your current situation and how I can best support you..." className="min-h-[160px] p-4 rounded-2xl bg-muted/40 border-transparent focus-visible:border-indigo-500 focus-visible:bg-background transition-colors text-base shadow-inner resize-y" {...field} />
            </FormControl>
            <FormMessage className="ml-1" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-2xl text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-full text-lg font-semibold bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/30 transition-all duration-300 hover:-translate-y-0.5">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Requesting Consultation...
          </> : "Request a Consultation"}
      </Button>
    </Form>;
}
export default function ConnectWithSupport() {
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="begin-your-personalized-consultation" className="relative dark bg-background text-foreground py-16 lg:py-24 overflow-hidden">
      <div className="absolute bottom-0 left-0 w-full max-w-3xl -translate-x-1/4 translate-y-1/4 opacity-[0.05] pointer-events-none z-0">
        <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-full fill-indigo-500">
          <path d="M44.7,-76.4C58.8,-69.2,71.8,-59.1,81.3,-46.3C90.8,-33.5,96.8,-18,96.5,-2.8C96.2,12.4,89.6,27.3,80.2,40.1C70.8,52.9,58.6,63.6,44.6,71.2C30.6,78.8,15.3,83.3,0.1,83.1C-15.1,82.9,-30.2,78,-43.8,70.2C-57.4,62.4,-69.5,51.7,-78.1,38.7C-86.7,25.7,-91.8,10.4,-91.5,-4.7C-91.2,-19.8,-85.5,-34.7,-76.1,-47.1C-66.7,-59.5,-53.6,-69.4,-39.6,-76.5C-25.6,-83.6,-12.8,-87.9,1.1,-89.5C15,-91.1,30.6,-83.6,44.7,-76.4Z" transform="translate(100 100)" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-12 gap-16 lg:gap-12 items-center">
          <div ref={contentRef} className={cn("lg:col-span-5 space-y-8 transition-all duration-1000 ease-out", isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
            <div className="space-y-4">
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-foreground tracking-tight leading-[1.1]">
                Start with <span className="text-indigo-400">Clarity</span>
              </h2>
              <p className="text-xl text-muted-foreground leading-relaxed max-w-xl font-sans">Schedule a free 15 minute introductory call to discuss your situation, review your current information, and determine how I can help you move forward with greater clarity and confidence.</p>
            </div>

            <div className="space-y-6 pt-2">
              {referralInfo.map((info, i) => <div key={i} data-index={i} className="flex items-start gap-5 group">
                  <div className="flex-shrink-0 size-14 rounded-3xl bg-indigo-500/10 flex items-center justify-center text-indigo-400 group-hover:bg-indigo-600 group-hover:text-white transition-colors duration-300 shadow-sm">
                    <info.icon className="size-6" />
                  </div>
                  <div className="space-y-1.5 pt-0.5">
                    <h3 className="text-xl font-serif font-semibold text-foreground">
                      {info.title}
                    </h3>
                    <p className="text-muted-foreground text-base leading-relaxed font-sans">
                      {info.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>

          <div ref={formRef} className={cn("lg:col-span-7 transition-all duration-1000 delay-200 ease-out", isFormVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
            <Card className="border-4 border-indigo-500/20 rounded-[3rem] shadow-2xl shadow-indigo-500/10 overflow-hidden bg-card/95 backdrop-blur-md">
              <CardContent className="p-6 sm:p-8 lg:p-10">
                <div className="mb-6">
                  <h3 className="text-2xl font-serif font-bold text-foreground mb-1">
                    Start the Conversation
                  </h3>
                  <p className="text-muted-foreground font-sans">
                    Share a few details about your situation, and I will reach
                    out to schedule a free introductory call to better
                    understand your needs and how I can help.
                  </p>
                </div>

                <FormProvider formKey="consultation_form_home" formId="consultation_form_home" sectionName="Connect With Support" defaultValues={{
                name: "",
                email: "",
                phone: "",
                subject: "",
                message: ""
              }} submitText="Request a Consultation" successMessage="Thank you for reaching out. I have received your information and will contact you shortly to coordinate your consultation.">
                  <ContactFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}